import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { SERVICES } from "./data";
import { Eyebrow, MaskLines, Reveal, ease } from "./motion";
import { Stage } from "./anims/Stage";

const useDesktop = () => {
  const q = "(min-width: 1024px)";
  const [d, setD] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const on = () => setD(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return d;
};

const Row = ({ s, i, on, onSelect, desktop }) => (
  <div className="border-t border-white/10">
    <button
      onClick={onSelect}
      data-testid={`service-row-${s.id}`}
      aria-expanded={on}
      className="group relative w-full text-left py-6 md:py-7 grid grid-cols-12 gap-3 items-baseline"
    >
      <span className={`col-span-2 font-mono text-[11px] sm:text-xs transition-colors ${on ? "text-cyan-300" : "text-slate-600"}`}>S/{String(i + 1).padStart(2, "0")}</span>
      <h3 className={`col-span-10 font-display text-[1.4rem] leading-tight sm:text-3xl font-semibold tracking-tight transition-[color,transform] duration-500 ${on ? "text-white translate-x-2" : "text-slate-500 group-hover:text-slate-300"}`}>{s.title}</h3>
      <span className={`absolute left-0 bottom-0 h-px w-full origin-left bg-cyan-300/70 transition-transform duration-700 ${on ? "scale-x-100" : "scale-x-0"}`} />
    </button>
    <AnimatePresence initial={false}>
      {on && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.5, ease }} className="overflow-hidden">
          <div className="pb-7 grid grid-cols-12 gap-3">
            <div className="col-span-10 col-start-3">
              <p className="text-slate-400 leading-relaxed">{s.body}</p>
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-cyan-300/80">{s.tags.join(" / ")}</p>
            </div>
            {!desktop && <div className="col-span-12 mt-5"><Stage id={s.id} title={s.title} testid={`capability-stage-mobile-${s.id}`} /></div>}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export const Services = () => {
  const [active, setActive] = useState(SERVICES[0].id);
  const current = SERVICES.find((s) => s.id === active);
  const desktop = useDesktop();

  return (
    <section id="services" className="relative py-24 md:py-36" data-testid="services-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14 md:mb-20">
          <div>
            <Eyebrow>Capabilities</Eyebrow>
            <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1]">
              <MaskLines lines={["Everything a business", <>network <span className="font-serif-i font-normal text-cyan-300">needs.</span></>]} />
            </h2>
          </div>
          <Reveal><p className="max-w-sm text-slate-400">From the first cable to day-to-day support — designed once, managed properly. Pick a capability to see it in action.</p></Reveal>
        </div>
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <Reveal className="lg:col-span-5 border-b border-white/10">
            {SERVICES.map((s, i) => <Row key={s.id} s={s} i={i} on={active === s.id} onSelect={() => setActive(s.id)} desktop={desktop} />)}
          </Reveal>
          <div className="hidden lg:block lg:col-span-7 lg:sticky lg:top-24">
            {desktop && <Reveal delay={0.1}><Stage id={active} title={current.title} /></Reveal>}
          </div>
        </div>
      </div>
    </section>
  );
};
